"use client";

import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch, RootState } from "@/store";
import { UseDataRangeFilter } from "@/types";
import { toast } from "react-toastify";
import {
  fetchFullCommission,
  fetchPaymentsHistoryCommmission,
} from "@/store/modules/fullCommission/thunks";
import { useAuth } from "@/context/AuthContext";

const useFullCommission = ({ dateRange, onSuccess }: UseDataRangeFilter) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page } = params;
  const { currency, establishment } = useAuth();
  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  const { fullCommission, paymentHistoryCommission, loading, error } =
    useSelector((state: RootState) => state.fullCommission);
  const dispatch: AppDispatch = useDispatch();

  const payload = useMemo(
    () => ({
      ...params,
      start_date: dateRange?.start?.toISOString().split("T")[0],
      end_date: dateRange?.end?.toISOString().split("T")[0],
    }),
    [params, dateRange?.start, dateRange?.end],
  );

  // Busca inicial apenas uma vez
  useEffect(() => {
    if (establishment?.id && dateRange?.start && dateRange?.end) {
      const initialPayload = {
        ...params,
        filter: {
          ...params.filter,
          establishment_id: establishment.id,
        },
        start_date: dateRange.start.toISOString().split("T")[0],
        end_date: dateRange.end.toISOString().split("T")[0],
      };
      dispatch(fetchFullCommission(initialPayload));
    }
  }, [establishment?.id]); // Empty dependency array - only run once on mount

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const handleSubmitFilter = async () => {
    const payload: Record<string, any> = {
      ...params,
      start_date: dateRange.start.toISOString().split("T")[0],
      end_date: dateRange.end.toISOString().split("T")[0],
    };

    try {
      await dispatch(fetchFullCommission(payload)).unwrap();
    } catch (err: unknown) {
      const error = err as {
        errors?: Record<string, string[]>;
        message?: string;
      };
      if (error?.errors) {
        Object.values(error.errors)
          .flat()
          .forEach((msg: string) => {
            toast.error(msg);
          });
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error("Erro ao buscar dados financeiros.");
      }
    }
  };

  const totalItems = fullCommission?.total ?? 0;
  const itemsPerPage = fullCommission?.perPage ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(
    fullCommission?.currentPage ?? 1,
  );

  useEffect(() => {
    const page = fullCommission?.currentPage;
    if (page && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [fullCommission?.currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  return {
    fullCommission,
    loading,
    error,
    handleSubmitFilter,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    currency,
    paymentHistoryCommission,
    handleFilterChange,
    searchValue,
  };
};

export default useFullCommission;
